<script setup lang="ts">
import { ref, watch } from 'vue'
import { useConsentStore } from '~~/stores/consentStore'
import { useQuickView } from '~~/app/composables/useQuickView'

const store = useConsentStore()
// Baner (position:fixed, z-index:9998) leży nad modalem QuickView (z-index 400) i na
// mobile zakrywa dolny panel, przechwytując dotyk → „nie da się swipeować panelu".
// Chowamy baner na czas otwartego QuickView; po zamknięciu wraca (stan w consentStore
// nietknięty). Toasty (z-index 9999) zostają nad modalem.
const { isOpen: quickViewOpen } = useQuickView()
const showSettings = ref(false)
const analytics = ref(false)
const marketing = ref(false)

function syncFromStore() {
  analytics.value = store.current.analytics
  marketing.value = store.current.marketing
}

function openSettings() {
  syncFromStore()
  showSettings.value = true
}

function saveSettings() {
  store.save({ analytics: analytics.value, marketing: marketing.value })
  showSettings.value = false
}

function close() {
  showSettings.value = false
  store.cancelReopen()
}

// Wejście z footera (reopen) → otwórz panel z aktualnym wyborem.
watch(
  () => store.forceOpen,
  (open) => {
    if (open) openSettings()
  },
  { immediate: true },
)
</script>

<template>
  <Transition name="cookie-consent">
    <section
      v-if="store.isBannerVisible && !quickViewOpen"
      class="cookie-consent"
      role="dialog"
      aria-label="Zgoda na pliki cookie"
      aria-live="polite"
    >
      <div class="cookie-consent__inner">
        <div class="cookie-consent__intro">
          <p class="cookie-consent__title">Szanujemy Twoją prywatność 🍪</p>
          <p class="cookie-consent__text">
            Używamy plików cookie do analizy ruchu i poprawy działania sklepu.
            Niezbędne działają zawsze. Szczegóły w
            <NuxtLink to="/polityka-prywatnosci" class="cookie-consent__link">
              polityce prywatności</NuxtLink>.
          </p>
        </div>

        <!-- Panel kategorii -->
        <div v-if="showSettings" data-testid="cookie-panel" class="cookie-consent__panel">
          <label class="cookie-consent__row cookie-consent__row--locked">
            <span class="cookie-consent__row-label">Niezbędne</span>
            <span class="cookie-consent__row-note">zawsze aktywne</span>
            <input type="checkbox" checked disabled aria-label="Niezbędne — zawsze aktywne">
          </label>
          <label class="cookie-consent__row">
            <span class="cookie-consent__row-label">Analityka</span>
            <input
              v-model="analytics"
              type="checkbox"
              data-testid="cookie-toggle-analytics"
              aria-label="Analityka (Google Analytics)"
            >
          </label>
          <label class="cookie-consent__row">
            <span class="cookie-consent__row-label">Marketing</span>
            <input
              v-model="marketing"
              type="checkbox"
              data-testid="cookie-toggle-marketing"
              aria-label="Marketing"
            >
          </label>
        </div>

        <!-- Akcje -->
        <div class="cookie-consent__actions">
          <button
            type="button"
            class="cookie-consent__btn cookie-consent__btn--primary"
            data-testid="cookie-accept-all"
            @click="store.acceptAll()"
          >
            Akceptuj wszystkie
          </button>
          <button
            type="button"
            class="cookie-consent__btn"
            data-testid="cookie-reject-all"
            @click="store.rejectAll()"
          >
            Odrzuć wszystkie
          </button>
          <button
            v-if="!showSettings"
            type="button"
            class="cookie-consent__btn cookie-consent__btn--ghost"
            data-testid="cookie-settings"
            @click="openSettings()"
          >
            Ustawienia
          </button>
          <button
            v-else
            type="button"
            class="cookie-consent__btn cookie-consent__btn--primary"
            data-testid="cookie-save"
            @click="saveSettings()"
          >
            Zapisz wybór
          </button>
        </div>

        <!-- Zamknięcie dostępne tylko po wcześniejszej decyzji (wejście z footera) -->
        <button
          v-if="store.hasDecided"
          type="button"
          class="cookie-consent__close"
          aria-label="Zamknij ustawienia cookies"
          @click="close()"
        >
          ×
        </button>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
/*
 * UWAGA: globalnie ładowany jest tylko app/assets/css/main.css (nuxt.config.ts:139);
 * tokens.css NIE jest wpięty. Dlatego używamy WYŁĄCZNIE zmiennych zdefiniowanych w
 * main.css (--color-black, --color-white, --font-weight-bold, --transition-*), a dla
 * odstępów/promienia/cienia — surowych wartości (jak SiteFooter / UpdateAvailableBanner).
 */
.cookie-consent {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 9998;
  background: var(--color-black);
  color: var(--color-white);
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(21, 21, 21, 0.16);
  max-width: 560px;
  margin-inline: auto;
}

.cookie-consent__inner {
  position: relative;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cookie-consent__title {
  margin: 0 0 8px;
  font-weight: var(--font-weight-bold);
  font-size: 1.0625rem;
}

.cookie-consent__text {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--color-white);
  opacity: 0.85;
}

.cookie-consent__link {
  color: var(--color-accent, #c9a96e);
  text-decoration: underline;
}

/* Akcent discanto (Dusty Mauve) ma na ciemnym banerze 3,53:1 — poniżej AA.
   Żaden kolor dodatkowy marki nie daje tu 4,5:1, więc link jest w Fog Silver
   (16,45:1) i odróżnia się podkreśleniem, jak w tekście obok. */
[data-brand="discanto"] .cookie-consent__link {
  color: var(--color-bg);
}

.cookie-consent__panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.cookie-consent__row {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9375rem;
}

.cookie-consent__row-label {
  flex: 1;
}

.cookie-consent__row-note {
  font-size: 0.8125rem;
  opacity: 0.6;
}

.cookie-consent__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cookie-consent__btn {
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: var(--color-white);
  font-size: 0.9375rem;
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

[data-brand="youlook"] .cookie-consent__btn {
  border-radius: 0;
}

.cookie-consent__btn:hover {
  opacity: 0.8;
}

.cookie-consent__btn--primary {
  background: var(--color-white);
  color: var(--color-black);
  border-color: var(--color-white);
}

.cookie-consent__btn--ghost {
  border-color: transparent;
  text-decoration: underline;
}

.cookie-consent__close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--color-white);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
}

.cookie-consent__close:hover {
  opacity: 1;
}

.cookie-consent-enter-active,
.cookie-consent-leave-active {
  transition: transform var(--transition-base), opacity var(--transition-base);
}

.cookie-consent-enter-from,
.cookie-consent-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

@media (max-width: 600px) {
  .cookie-consent__actions {
    flex-direction: column;
  }

  .cookie-consent__btn {
    width: 100%;
  }
}
</style>
